import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { licensesApi } from '../api'
import { PERPETUAL_EXPIRY_DATE, isPerpetual, toKstDateInput } from '../config/license'
import type { LicenseIssuance } from '../types'

interface FormProps {
  serverId: string
  // 있으면 재발급(만료일·Node 수 변경) — 현재 값으로 채운다
  current?: LicenseIssuance
  onDone?: () => void
  onCancel?: () => void
}

/**
 * Cloud 라이센스 발급/재발급 폼. 재발급도 같은 발급 API 로 기존 라이센스를 교체하고 Edge 에 바로 전달된다.
 * 모달(라이센스 변경)과 서버 상세의 활성화 패널(최초 발급)이 함께 쓴다.
 */
export function CloudLicenseForm({ serverId, current, onDone, onCancel }: FormProps) {
  const qc = useQueryClient()
  const isReissue = !!current
  const [perpetual, setPerpetual] = useState(current ? isPerpetual(current.expiresAt) : false)
  const [expiresAt, setExpiresAt] = useState(
    current && !isPerpetual(current.expiresAt) ? toKstDateInput(current.expiresAt) : '')
  const [maxNodeCount, setMaxNodeCount] = useState(String(current?.maxNodeCount ?? 50))
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => licensesApi.issueCloud(
      serverId, perpetual ? PERPETUAL_EXPIRY_DATE : expiresAt, parseInt(maxNodeCount)
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] })
      qc.invalidateQueries({ queryKey: ['licenses'] })
      onDone?.()
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      setError(err.response?.data?.error ?? `Cloud 라이센스 ${isReissue ? '변경' : '발급'}에 실패했습니다.`)
    },
  })

  return (
    <div>
      {error && (
        <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2 mb-3">{error}</p>
      )}
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs text-gray-500">만료일</label>
            <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={perpetual} onChange={e => setPerpetual(e.target.checked)} />
              무기한
            </label>
          </div>
          <input type="date" value={perpetual ? '' : expiresAt} disabled={perpetual}
            onChange={e => setExpiresAt(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">최대 Node 수</label>
          <input type="number" value={maxNodeCount}
            onChange={e => setMaxNodeCount(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="flex gap-2 mt-5">
        <button
          onClick={() => mutation.mutate()}
          disabled={(!perpetual && !expiresAt) || mutation.isPending}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
          {mutation.isPending ? '처리 중…' : isReissue ? '변경' : '발급'}
        </button>
        {onCancel && (
          <button onClick={onCancel}
            className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">취소</button>
        )}
      </div>
    </div>
  )
}

interface ModalProps {
  server: { id: string; name: string }
  current?: LicenseIssuance
  onClose: () => void
}

export default function CloudLicenseModal({ server, current, onClose }: ModalProps) {
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-surface rounded-xl p-6 w-full max-w-sm shadow-xl">
        <h3 className="font-semibold text-gray-800 mb-1">
          {current ? 'Cloud 라이센스 변경' : 'Cloud 라이센스 발급'}
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          {server.name}
          <span className="ml-2 font-mono">· {server.id.slice(0, 20)}…</span>
        </p>
        {current && (
          <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2 mb-3">
            새 라이센스로 교체되어 Edge 에 바로 전달됩니다. 오프라인이면 다음 접속 시 전달됩니다.
          </p>
        )}
        <CloudLicenseForm serverId={server.id} current={current} onDone={onClose} onCancel={onClose} />
      </div>
    </div>
  )
}
